import { useEffect } from "react";

// Global dialog a11y for the app's hand-built overlays (`fixed inset-0` with content): role/aria-modal,
// initial focus, Tab focus-trap, Escape to dismiss, focus restore on close. Radix dialogs manage themselves.
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),[contenteditable=true]';
const CLOSE = '[data-dialog-close],[aria-label^="Close" i],[data-testid$="-close"],[data-testid$="close-btn"],[data-testid$="-cancel"]';

const isOverlay = (el) => {
  if (!(el instanceof HTMLElement) || typeof el.className !== "string") return false;
  if (!/(^|\s)fixed(\s|$)/.test(el.className) || !/(^|\s)inset-0(\s|$)/.test(el.className)) return false;
  if (el.closest("[data-radix-popper-content-wrapper]") || el.getAttribute("role") === "dialog" && !el.dataset.a11yModal) return false;
  const cs = getComputedStyle(el);
  return cs.position === "fixed" && cs.display !== "none" && cs.visibility !== "hidden";
};
const visibleFocusables = (el) => [...el.querySelectorAll(FOCUSABLE)].filter((n) => n.getClientRects().length && !n.closest("[aria-hidden=true]"));
const radixOpen = () => document.querySelector('[role=dialog][data-state=open]:not([data-a11y-modal]),[role=alertdialog][data-state=open]');

export function ModalA11y() {
  useEffect(() => {
    const tracked = new Map(); // el -> element focused before it opened
    let n = 0;
    const scan = () => {
      const live = [...document.querySelectorAll("div.fixed.inset-0,span.fixed.inset-0")].filter(isOverlay);
      for (const el of live) {
        if (tracked.has(el)) continue;
        tracked.set(el, document.activeElement);
        if (!el.childElementCount) continue; // bare click-away backdrop of a menu
        el.dataset.a11yModal = "1";
        el.setAttribute("role", "dialog");
        el.setAttribute("aria-modal", "true");
        if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
        if (!el.hasAttribute("aria-label") && !el.hasAttribute("aria-labelledby")) {
          const h = el.querySelector("h1,h2,h3");
          if (h) { h.id = h.id || `a11y-dialog-title-${++n}`; el.setAttribute("aria-labelledby", h.id); }
          else el.setAttribute("aria-label", "Dialog");
        }
        if (!el.contains(document.activeElement)) (el.querySelector("[autofocus]") || visibleFocusables(el)[0] || el).focus({ preventScroll: true });
      }
      for (const [el, prev] of tracked) {
        if (live.includes(el)) continue;
        tracked.delete(el);
        if (el.dataset.a11yModal && prev instanceof HTMLElement && prev.isConnected && !document.activeElement?.closest?.("[data-a11y-modal]")) prev.focus({ preventScroll: true });
      }
    };
    const top = (withContent) => [...tracked.keys()].filter((el) => el.isConnected && (!withContent || el.childElementCount)).pop();
    const onKey = (e) => {
      if (e.defaultPrevented || radixOpen()) return;
      if (e.key === "Escape") {
        const el = top(false);
        if (!el) return;
        const btn = el.childElementCount ? [...el.querySelectorAll(CLOSE)].find((b) => b.getClientRects().length) : null;
        if (btn) btn.click(); else el.click();
        e.preventDefault();
      } else if (e.key === "Tab") {
        const el = top(true);
        if (!el) return;
        const f = visibleFocusables(el);
        if (!f.length) { e.preventDefault(); el.focus(); return; }
        const i = f.indexOf(document.activeElement);
        if (e.shiftKey && (i <= 0)) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && (i === -1 || i === f.length - 1)) { e.preventDefault(); f[0].focus(); }
      }
    };
    let raf = 0;
    const mo = new MutationObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(scan); });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
    window.addEventListener("keydown", onKey);
    scan();
    return () => { mo.disconnect(); window.removeEventListener("keydown", onKey); cancelAnimationFrame(raf); };
  }, []);
  return null;
}
